import { ComponentInstance, computed, defineCustomElement, nextTick, onBeforeMount, onMounted, onUnmounted, ref, useHost, watch } from "vue";
import { getIdGenerator } from "../../skyward.base.extensions.idGenerator";
import { convertTo12Hour, now } from "../../skyward.base.time";
import VueModalLayer from "../core/modalLayer";
import Svg from "../core/svg";
import { OptionalVueProp } from "../core/vueHelpers";
import TimePickerModal from "./TimePickerModal";
import { TimeSelectorExposed } from "./components/TimeSelector";

const TimePickerClasses =
{
    input: "timePicker",
    svg: "dateTimePicker__svg"
};

enum TimeParts
{
    Hour = "H",
    Minute = "M",
    AmPm = "X"
}

export interface Time
{
    hour?: number;
    minute?: number;
    amPm?: "AM" | "PM";
}

const TimePicker = /*#__PURE__*/ defineCustomElement({
    name: "sky-timepicker",
    shadowRoot: false,
    inheritAttrs: false,
    props:
    {
        name: OptionalVueProp(String),
        value: {
            type: String,
            required: false,
            default: null,
            validator(value: any)
            {
                const isValid = isValidTime(value);
                return !value || typeof value === "string" && isValid;
            }
        },
        autocomplete: OptionalVueProp(String),
        // TODO parse
        dependents: OptionalVueProp(Object),
        ariaLabelledby: OptionalVueProp(String),
        contained: OptionalVueProp(Boolean)
    },
    setup(props, context)
    {
        const inputRef = ref<HTMLInputElement>();
        const modalId = ref<string>();
        const idGenerator = getIdGenerator("popup", "_timePicker");
        let rootElem = useHost() as any as (HTMLElement & { value: string | undefined }) | null;
        const resolveRootElem = () =>
        {
            if (!rootElem)
            {
                rootElem = inputRef.value?.closest("sky-timepicker") as (HTMLElement & { value: string | undefined }) | null;
            }
            return rootElem;
        };
        const getAriaReadonly = () => resolveRootElem()?.getAttribute("aria-readonly")?.toLowerCase() === "true";
        const readonly = ref(getAriaReadonly());
        // Read from the host element rather than declaring an "id" prop, which would shadow the native HTMLElement.id property
        const getHostId = () => resolveRootElem()?.id || undefined;
        const hostId = ref(getHostId());

        const value = computed<string | undefined>({
            get: () => props.value,
            set: (newValue) =>
            {
                const currentRoot = resolveRootElem();
                if (currentRoot)
                {
                    currentRoot.value = newValue;
                }
            }
        });

        watch(() => props.value, (newVal) =>
        {
            if (!(!newVal || typeof newVal === "string" && isValidTime(newVal)))
            {
                value.value = "";
            }
        },
        {
            flush: "sync",
            immediate: true
        });

        const time = ref<Time>(getTime(props.value));

        watch(() => time.value, async (newTime) =>
        {
            if (newTime.hour === undefined || newTime.minute == undefined || newTime.amPm == undefined)
            {
                newTime.amPm = undefined;
                newTime.hour = undefined;
                newTime.minute = undefined;
                value.value = "";
            }
            else
            {
                const newTimeString = formatTime(newTime);
                value.value = newTimeString;
            }

            await nextTick();
            const event = new CustomEvent("change", { bubbles: true, detail: { internal: true } });
            resolveRootElem()?.dispatchEvent(event);
        },
        {
            deep: true
        });

        const modalOpen = ref<true | false | "closing">(false);

        watch(() => modalOpen.value, (newVal) =>
        {
            if (newVal)
            {
                modalId.value = idGenerator.getNewId();
            }
            else
            {
                modalId.value = undefined;
            }
        });

        // used to suppress the focusin event after the modal closes and refocuses the input
        const closeModal = async () =>
        {
            modalOpen.value = "closing";
            await nextTick();

            inputRef.value?.focus();
            modalOpen.value = false;
            inputRef.value?.setSelectionRange(0, inputRef.value.value.length);
        }

        // needs to be exposed as a function, otherwise Vue will deref it
        context.expose(
        {
            _closeModal: closeModal
        });

        onBeforeMount(() =>
        {
            if (hostId.value)
            {
                $.findLabel(hostId.value).attr("for", `${hostId.value}_input`);
            }
        });

        const observer = new MutationObserver(() =>
        {
            readonly.value = getAriaReadonly();
            hostId.value = getHostId();
        });

        onMounted(() =>
        {
            if (props.ariaLabelledby)
            {
                inputRef.value?.setAttribute("aria-labelledby", props.ariaLabelledby);
                resolveRootElem()?.removeAttribute("aria-labelledby");
            }

            const currentRoot = resolveRootElem();
            if (currentRoot)
            {
                observer.observe(currentRoot, { attributes: true, attributeFilter: ["aria-readonly", "id"] });
            }
        });

        onUnmounted(() =>
        {
            observer.disconnect();
        });

        const timepickerRef = ref<ComponentInstance<typeof TimePickerModal> & TimeSelectorExposed>();

        return () =>
        (
            <>
                <input
                    type="text"
                    class={TimePickerClasses.input}
                    placeholder="HH:MM XM"
                    readonly={readonly.value}
                    autocomplete={props.autocomplete ?? "off"}
                    ref={inputRef}
                    onClick={handleClick}
                    onFocusin={handleFocusIn}
                    onInput={handleInput}
                    onBeforeinput={handleBeforeInput}
                    onChange={handleChange}
                    onKeydown={handleKeydown}
                    onKeypress={handleKeypress}
                    onPaste={handlePaste}
                    value={value.value}
                    id={hostId.value ? `${hostId.value}_input` : undefined}
                    data-dependents={JSON.stringify(props.dependents)}
                    aria-expanded={modalOpen.value === true}
                    aria-haspopup="dialog"
                    aria-controls={modalId.value}
                />
                {
                    (modalOpen.value === true) &&
                    <VueModalLayer
                        expose={[getModalAnchorElement()]}
                        anchorTo={getModalAnchorElement()}
                        preserveFocus={(reason) => reason === "escape"}
                        onClose={() =>
                        {
                            closeModal();
                        }}
                    >
                        <TimePickerModal
                            ref={timepickerRef}
                            id={modalId.value}
                            inputRef={inputRef}
                            close={closeModal}
                            time={time.value}
                            onUpdate:time={(newTime) =>
                            {
                                time.value = newTime;
                            }}
                        />
                    </VueModalLayer>
                }
                <Svg
                    name="Time"
                    aria-haspopup="menu"
                    role="button"
                    showHoverEffect={false}
                    class={TimePickerClasses.svg}
                    onClick={() => toggleModal("svg click")}
                    isButton={!readonly.value}
                    aria-label="Time Picker"
                />
            </>
        );

        function getModalAnchorElement()
        {
            const container = inputRef.value!.parentElement!;
            return props.contained
                ? container.parentElement!
                : container;
        }

        function setValueFromUserInput(timeString: string)
        {
            if (timeString === "")
            {
                const newTime: Time =
                {
                    hour: undefined,
                    minute: undefined,
                    amPm: undefined
                };

                time.value = newTime;
                inputRef.value!.value = "";
                return;
            }

            const { hour: currentHour, minute: currentMinute } = getTimePartsFromValue(timeString);

            const partsAmPm = timeString.split(" ");
            const partsTime = timeString.split(":");
            let ending: "AM" | "PM" | undefined = undefined;

            const convertedHour = convertTo12Hour(currentHour);
            if (convertedHour !== currentHour)
            {
                ending = currentHour >= 12 ? "PM" : "AM";
            }

            if (partsAmPm.length == 2)
            {
                const endingPart = partsAmPm[1];
                if (endingPart === "PM" || endingPart === "AM")
                {
                    ending = endingPart;
                }
            }
            else if (!ending)
            {
                // If the time starts with 0, honor 24-hour time
                if (partsTime[0].startsWith("0") || (convertedHour > 5 && convertedHour !== 12))
                {
                    ending = "AM";
                }
                else
                {
                    ending = "PM";
                }
            }

            const newTime: Time =
            {
                hour: convertedHour,
                minute: currentMinute,
                amPm: ending ?? "PM"
            };

            time.value = newTime;
            inputRef.value!.value = formatTime(newTime);
        }

        function getTimePartsFromValue(timeString: string)
        {
            const value = timeString.replace(/[^\d:]/g, "");
            const parts = value.split(":");
            let hour = 0;
            let minute = 0;

            if (parts.length > 0)
            {
                hour = parseInt(parts[0]);
            }

            if (parts.length > 1)
            {
                minute = parseInt(parts[1].padEnd(2, "0"));
            }

            return { hour, minute };
        }

        function handlePaste(event: ClipboardEvent)
        {
            event.preventDefault();

            const pastedText = event.clipboardData?.getData('text/plain');
            const date = new Date("1/1/2000 " + pastedText);
            if (!pastedText || !date.isValidDate())
            {
                return false;
            }

            const hours24 = date.getHours();
            const hours12 = (hours24 % 12) || 12;
            const period = hours24 >= 12 ? 'PM' : 'AM';

            const timeString = `${hours12}:${date.getMinutes()} ${period}`;

            setValueFromUserInput(timeString);
            return false;
        }

        function handleFocusIn(event: FocusEvent)
        {
            const element = event.target as HTMLElement;
            const suppressFocus = element.getAttribute("data-suppress-focus") === "true" || resolveRootElem()?.getAttribute("data-suppress-focus") === "true";
            if (suppressFocus)
            {
                element.removeAttribute("data-suppress-focus");
                resolveRootElem()?.removeAttribute("data-suppress-focus");
                return;
            }

            handleOpenModal("focusin");
        }

        function handleChange(event: Event)
        {
            event.preventDefault();
            event.stopPropagation();
            const rawValue = inputRef.value!.value;

            setValueFromUserInput(rawValue);
        }

        function setCurrentTime()
        {
            const today = now();
            const currentHour = today.getHours();

            const newTime: Time =
            {
                hour: convertTo12Hour(currentHour),
                minute: today.getMinutes(),
                amPm: currentHour >= 12 ? "PM" : "AM",
            };

            time.value = newTime;
        }

        function handleKeypress(event: KeyboardEvent)
        {
            const key = event.key.toUpperCase();

            // Don't propagate the event to the modal if we are focused on the Input
            if (event.target === inputRef.value)
            {
                if (key === "P" || key === "A")
                {
                    event.stopPropagation();
                }
            }
        }

        function handleKeydown(event: KeyboardEvent)
        {
            const key = event.key.toUpperCase();

            if (!modalOpen.value && event.altKey)
            {
                if (key === "ARROWDOWN" || key === "ARROWUP")
                {
                    event.preventDefault();
                    handleOpenModal("keypress");
                    return;
                }
            }

            if (readonly.value)
            {
                return;
            }

            const start = (event.target! as HTMLInputElement).selectionStart!;
            const end = (event.target! as HTMLInputElement).selectionEnd!;
            const { hour: currentHour, minute: currentMinute } = getTimePartsFromValue((event.target! as HTMLInputElement).value);

            switch (key)
            {
                case "P":
                    if (currentHour != time.value.hour || currentMinute != time.value.minute)
                    {
                        return;
                    }
                    time.value.amPm = "PM";
                    return;
                case "A":
                    if (currentHour != time.value.hour || currentMinute != time.value.minute)
                    {
                        return;
                    }
                    time.value.amPm = "AM";
                    return;
                case "C":
                case "T":
                case "N":
                    setCurrentTime();
                    event.stopPropagation();
                    return;
                case "ARROWRIGHT":
                case "ARROWLEFT":
                    event.preventDefault();
                    event.stopPropagation();

                    handleSelection(key === "ARROWLEFT", key === "ARROWRIGHT", start, end);
                    return;
                case "ENTER":
                    event.preventDefault();
                    event.stopPropagation();
                    modalOpen.value = false;
                    setValueFromUserInput(inputRef.value!.value);
                    return;
                case "TAB":
                    modalOpen.value = false;
                    setValueFromUserInput(inputRef.value!.value);
                    return;
                case "ARROWUP":
                case "ARROWDOWN":
                    event.preventDefault();
                    event.stopPropagation();

                    if (start > 0 && end > 0)
                    {
                        const currentPart = getSelectedPart(start, end);
                        switch (currentPart)
                        {
                            case TimeParts.Hour:
                                timepickerRef.value?.focusHourInput();
                                break;
                            case TimeParts.Minute:
                                timepickerRef.value?.focusMinuteInput();
                                break;
                            case TimeParts.AmPm:
                                timepickerRef.value?.focusAmPmToggle();
                                break;
                        }
                    }
                    else
                    {
                        timepickerRef.value?.focusHourInput();
                    }
            }
        }

        function handleBeforeInput(event: Event)
        {
            const inputEvent = event as InputEvent;
            const inputData = inputEvent.data?.toUpperCase();
            const input = event.target as HTMLInputElement;

            // If data is being entered into the input, validate it
            if (inputData)
            {
                if (isNaN(parseInt(inputData)))
                {
                    // Allow the ":", "P", or "A" character if it's the first one
                    if (inputData === ":" && !input.value.includes(":") && input.value.length > 0)
                    {
                        return;
                    }
                    else if (inputData === "P" && !input.value.includes("P") && input.value.length > 0)
                    {
                        return;
                    }
                    else if (inputData === "A" && !input.value.includes("A") && input.value.length > 0)
                    {
                        return;
                    }

                    inputEvent.preventDefault(); // Stops the character from being inserted
                    return;
                }
                else
                {
                    const currentValue = input.value.replace(/[^\d:]/g, "");
                    const selectionEnd = input.selectionEnd!;
                    const selectionStart = input.selectionStart!;
                    const totalSelection = selectionEnd - selectionStart;

                    // If the user is overriding the entire input value, allow it
                    if (totalSelection === input.value.length)
                    {
                        return;
                    }

                    const parts = currentValue.split(":");
                    let minutePartSelectionStart = 3;
                    let minutePartSelectionEnd = 5;

                    if (parts.length > 0)
                    {
                        minutePartSelectionStart = parts[0].length + 1;
                        minutePartSelectionEnd = minutePartSelectionStart + 2;
                    }

                    const selectedPart = getSelectedPart(selectionStart, selectionEnd);

                    if (parts.length > 1 && selectionStart >= minutePartSelectionStart && selectionEnd <= minutePartSelectionEnd)
                    {
                        const minutePart = parseInt(parts[1]);
                        const inputValue = parseInt(inputData);

                        // If the entire minute part is selected, AND the user is entering a valid minute, allow it.
                        if (selectionStart === minutePartSelectionStart && selectionEnd === minutePartSelectionEnd && inputValue < 6)
                        {
                            return;
                        }
                    }
                    else if (selectedPart === TimeParts.AmPm && inputData !== "P" && inputData !== "A")
                    {
                        inputEvent.preventDefault(); // Stops the character from being inserted
                        return;
                    }
                }
            }
        }

        async function handleInput(event: Event)
        {
            const input = event.target as HTMLInputElement;
            let selectionEnd = input.selectionEnd!;
            const newValue = input.value.replace(/[^\d:]/g, "");
            const inputEvent = event as InputEvent;
            const newData = inputEvent.data ?? "";
            const uppercaseData = newData.toUpperCase();

            const partsAmPm = input.value.split(" ");
            let ending: "AM" | "PM" | undefined = undefined;
            let updatedEnding = false;
            if (uppercaseData === "P")
            {
                updatedEnding = true;
                ending = "PM";
            }
            else if (uppercaseData === "A")
            {
                updatedEnding = true;
                ending = "AM";
            }
            else if (partsAmPm.length == 2)
            {
                const endingPart = partsAmPm[1];
                if (endingPart === "PM" || endingPart === "AM")
                {
                    ending = endingPart;
                }
            }
            // If the user typed "A" or "P" in the input manually...
            else if (input.value.toUpperCase().endsWith("P"))
            {
                updatedEnding = true;
                ending = "PM";
            }
            else if (input.value.toUpperCase().endsWith("A"))
            {
                updatedEnding = true;
                ending = "AM";
            }

            const parts = newValue.split(":");

            if (updatedEnding)
            {
                if (parts.length > 0)
                {
                    parts[0] = parts[0].padStart(2, "0");
                }
                if (parts.length > 1)
                {
                    parts[1] = parts[1].padStart(2, "0");
                }
                else
                {
                    parts[1] = "00";
                }
            }

            // If the user is attempting to enter more than 1 colon, remove their selection entirely.
            if (parts.length > 2)
            {
                event.preventDefault();
                let valueToSet = newValue.slice(0, selectionEnd);
                valueToSet = valueToSet.slice(0, valueToSet.length - newData.length) + newValue.slice(selectionEnd);
                input.value = valueToSet;
                selectionEnd -= newData.length;
                input.setSelectionRange(selectionEnd, selectionEnd);
                return;
            }

            let remainingHour = "";

            // Handle the HOUR portion
            if (parts.length > 0)
            {
                const hourPart = parts[0];
                // If the HOUR portion starts with a non-0, non-1, or non-2 (sometimes) character, that should be it for the portion
                if (!hourPart.startsWith("0") && !hourPart.startsWith("1") && !hourPart.startsWith("2"))
                {
                    remainingHour = hourPart.slice(1);
                    parts[0] = hourPart.slice(0, 1);

                    // If there is more left, it should push the rest to the MINUTE portion
                    if (remainingHour.length > 0 && parts.length >= 2)
                    {
                        parts[1] = `${remainingHour}${parts[1]}`;
                    }
                }
                else if (hourPart.startsWith("2"))
                {
                    const additionalHourPortion = hourPart.slice(1);
                    let adjusted = false;
                    if (additionalHourPortion && additionalHourPortion.length === 1)
                    {
                        const parsedAdditionalHourPortion = parseInt(additionalHourPortion);
                        if (parsedAdditionalHourPortion > 3)
                        {
                            adjusted = true;
                            remainingHour = hourPart.slice(1);
                            parts[0] = hourPart.slice(0, 1);

                            // If there is more left, it should push the rest to the MINUTE portion
                            if (remainingHour.length > 0 && parts.length >= 2)
                            {
                                parts[1] = `${remainingHour}${parts[1]}`;
                            }
                        }
                    }

                    if (!adjusted)
                    {
                        remainingHour = hourPart.slice(2);
                        parts[0] = hourPart.slice(0, 2);
                    }
                }
                else
                {
                    remainingHour = hourPart.slice(2);
                    parts[0] = hourPart.slice(0, 2);
                }

                if (remainingHour.length > 0 && parts.length < 2)
                {
                    parts.push(remainingHour);
                }
            }
            // Handle the MINUTE portion
            if (parts.length > 1)
            {
                if (remainingHour.length > 0)
                {
                    parts[1] = remainingHour + parts[1].slice(1, 2);
                    selectionEnd = 4;
                }
                else
                {
                    parts[1] = parts[1].slice(0, 2);
                }

                if (parts[1].length === 1 && parseInt(parts[1]) > 5)
                {
                    parts[1] = parts[1].padStart(2, "0");
                    selectionEnd++;
                }
                else if (parts[1].length === 2 && parseInt(parts[1]) > 59)
                {
                    parts[1] = newData.padStart(2, "0");
                    selectionEnd++;
                }
            }

            let selectionStart = selectionEnd;

            let value = parts.join(":");

            if (ending)
            {
                value += ` ${ending}`;
            }

            input.value = value;
            await nextTick();

            // If the user typed AM or PM, let select the end of the control
            if (uppercaseData === "P" || uppercaseData === "A")
            {
                selectionStart = input.value.length;
                selectionEnd = input.value.length;
            }

            input.setSelectionRange(selectionStart, selectionEnd);
        }

        function handleClick(event: MouseEvent)
        {
            const start = (event.target! as HTMLInputElement).selectionStart!;
            const end = (event.target! as HTMLInputElement).selectionEnd!;

            handleSelection(false, false, start, end);
        }

        /**
         * Handle selecting the input range.
         * (Optional) based on a direction from your current selection
         * @param left true if moving left
         * @param right true if moving right
         */
        function handleSelection(left: boolean, right: boolean, start: number, end: number)
        {
            const input = inputRef.value!;
            const currentValue = input.value.replace(/[^\d:]/g, "");
            const firstColon = currentValue.indexOf(":");

            // There are no parts to focus;
            if (firstColon === -1)
            {
                return;
            }

            const currentPart = getSelectedPart(start, end);
            if (start == end)
            {
                selectPart(currentPart);
                return;
            }

            if (right)
            {
                switch (currentPart)
                {
                    case TimeParts.Hour:
                        selectPart(TimeParts.Minute);
                        break;
                    case TimeParts.Minute:
                        selectPart(TimeParts.AmPm);
                        break;
                    case TimeParts.AmPm:
                        selectPart(TimeParts.Hour);
                        break;
                }

                return;
            }
            else if (left)
            {
                switch (currentPart)
                {
                    case TimeParts.Hour:
                        selectPart(TimeParts.AmPm);
                        break;
                    case TimeParts.Minute:
                        selectPart(TimeParts.Hour);
                        break;
                    case TimeParts.AmPm:
                        selectPart(TimeParts.Minute);
                        break;
                }

                return;
            }

            // If no direction was specified, select based on current selection
            selectPart(currentPart);
        }

        function getSelectedPart(start: number, end: number)
        {
            const input = inputRef.value!;
            const currentValue = input.value.replace(/[^\d:]/g, "");
            const firstColon = currentValue.indexOf(":");
            const firstSpace = input.value.indexOf(" ");

            if (isInRange(input, start, end, 0, firstColon))
            {
                return TimeParts.Hour;
            }
            else if (firstSpace !== -1 && isInRange(input, start, end, firstSpace + 1, input.value.length))
            {
                return TimeParts.AmPm;
            }
            else
            {
                return TimeParts.Minute;
            }
        }

        function selectPart(part: TimeParts)
        {
            const input = inputRef.value!;
            const currentValue = input.value.replace(/[^\d:]/g, "");

            const firstColon = currentValue.indexOf(":");
            const firstSpace = input.value.indexOf(" ");
            const valueEnd = currentValue.length;

            let startSelection = null;
            let endSelection = null;


            switch (part)
            {
                case TimeParts.Hour:
                    startSelection = 0;
                    endSelection = firstColon !== -1 ? firstColon : valueEnd;
                    input.setSelectionRange(startSelection, endSelection); // Highlight HH
                    break;
                case TimeParts.Minute:
                    if (firstColon !== -1)
                    {
                        startSelection = firstColon + 1;

                        input.setSelectionRange(startSelection, firstSpace === -1 ? valueEnd : firstSpace); // Highlight MM
                    }
                    break;
                case TimeParts.AmPm:
                    if (firstSpace !== -1)
                    {
                        startSelection = firstSpace + 1;

                        input.setSelectionRange(startSelection, input.value.length); // Highlight MM
                    }
                    break;
            }
        }

        function isInRange(_input: HTMLInputElement, start: number, end: number, startRange: number, endRange: number)
        {
            return (start >= startRange && end <= endRange);
        }

        function toggleModal(cause: string)
        {
            if (modalOpen.value === true)
            {
                closeModal();

                return;
            }

            handleOpenModal(cause);
        }

        function handleOpenModal(cause: string)
        {
            if (readonly.value || modalOpen.value !== false)
            {
                return;
            }
            modalOpen.value = true;
            rootElem?.scrollIntoView({ block: 'nearest' });

            if (cause === "svg click" && inputRef.value)
            {
                inputRef.value.focus();
            }
        }
    }
});

class SkyTimePicker extends TimePicker
{
    connectedCallback()
    {
        // Clear any pre-existing inner HTML (e.g., serialized by CKEditor) before Vue mounts,
        // so that Vue's own render does not produce duplicate elements.
        if (!(this as any)._instance)
        {
            this.innerHTML = '';
        }
        super.connectedCallback();
    }
    /**
     * Close the datepicker modal programatically.
     * This calls the function exposed from the component setup via `context.expose`.
     */
    closeModal(): Promise<void> | void
    {
        const fn = (this as any)._closeModal;
        if (typeof fn === "function")
        {
            return fn();
        }
    }
}

export default SkyTimePicker;

function isValidTime(timeString: string)
{
    if (timeString == "")
    {
        return true;
    }

    const timeRegex = /^(0[1-9]|1[0-2]):[0-5][0-9] (AM|PM)$/;
    return timeRegex.test(timeString);
}

function getHour(timeString: string)
{
    const time = timeString.replace(/[^\d:]/g, "");
    const parts = time.split(":");
    if (parts.length > 0)
    {
        return parseInt(parts[0]);
    }

    return undefined;
}

function getMinute(timeString: string)
{
    const time = timeString.replace(/[^\d:]/g, "");
    const parts = time.split(":");
    if (parts.length > 1)
    {
        return parseInt(parts[1]);
    }

    return undefined;
}

function getAmPm(timeString: string)
{
    const parts = timeString.split(" ");
    if (parts.length > 1)
    {
        return parts[1];
    }

    return undefined;
}

export function getTime(timeStringParam: string)
{
    let timeString = timeStringParam;
    // If this is a DateTime string, remove the Date portion.
    if (timeString.includes('/'))
    {
        const firstSpace = timeString.indexOf(' ');
        timeString = timeString.substring(firstSpace);
    }

    const time: Time =
    {
        hour: getHour(timeString),
        minute: getMinute(timeString),
        amPm: getAmPm(timeString) as "AM" | "PM" | undefined
    };

    return time;
}

function formatTime(time: Time)
{
    return `${String(time.hour).padStart(2, "0")}:${String(time.minute).padStart(2, "0")} ${time.amPm}`;
}
